import Joi from 'joi';
import getConfig from 'next/config';
import _ from 'underscore';
import { startOfDay, endOfDay, parseISO } from 'date-fns';

import {
	api as apiRequest,
	batchApi as batchApiRequest
} from '@culture/common-helpers/request';
import { broadcastTypesHash } from '@culture/utils/helpers/broadcast';
import {
	preparePathParams,
	getBreadcrumbsParams,
	getMetaParams
} from '@culture/common-helpers/params';
import { filterQuery } from '@culture/common-helpers/url';
import Template from 'pageTemplates/schedule';
import { getCatalogMeta, getBreadcrumbs, getRubrics } from 'logic/page';
import { getFilterDateParams } from 'utils/helpers/filters';

const { serverRuntimeConfig } = getConfig();

const LiveSchedulePage = (props) => <Template {...props} />;

const getBroadcasts = (params) => {
	const broadcastParams = {
		fields: [
			'title',
			'broadcastType',
			'thumbnailFile',
			'institute',
			'name',
			'fromDate',
			'toDate',
			'publishDate'
		].join(),
		statuses: ['published'],
		expand: ['institute'],
		cached: true
	};

	const preparedBroadcastParams = preparePathParams(
		{
			...broadcastParams,
			..._(params).omit('type')
		},
		_(params).pick('type')
	);

	return apiRequest('broadcast', preparedBroadcastParams);
};

export async function getServerSideProps({ query, res }) {
	const validationSchema = Joi.object().keys({
		type: Joi.string().valid(..._(broadcastTypesHash).keys()),
		startDate: Joi.date(),
		endDate: Joi.date(),
		cleanCache: Joi.boolean(),
		page: Joi.number().default(1),
		limit: Joi.number().default(24),
		sort: Joi.string().default('publishDate'),
		query: Joi.string(),
		isNationalProjectParticipant: Joi.boolean()
	});

	let params = Joi.attempt(query, validationSchema, {
		stripUnknown: true
	});

	params = filterQuery({
		...params,
		...getFilterDateParams(params.startDate, params.endDate)
	});

	let modifiedParams = _(params).omit('startDate', 'endDate');

	if (params.startDate) {
		modifiedParams = filterQuery({
			...modifiedParams,
			publishDateFrom:
				params.startDate &&
				startOfDay(parseISO(params.startDate)).toISOString(),
			publishDateTo:
				params.endDate && endOfDay(parseISO(params.endDate)).toISOString()
		});
	} else {
		modifiedParams = { ...modifiedParams, isPassed: false };
	}

	const rubricPath = 'live/schedule';

	const [
		broadcasts,
		catalogMeta,
		breadcrumbs,
		rubrics
	] = await batchApiRequest([
		getBroadcasts(modifiedParams),
		getCatalogMeta(
			getMetaParams(
				rubricPath,
				params,
				_(modifiedParams).pick('type', 'publishDateFrom', 'publishDateTo')
			)
		),
		getBreadcrumbs(getBreadcrumbsParams(rubricPath, params)),
		getRubrics(rubricPath)
	]);

	res.setHeader('Cache-Control', serverRuntimeConfig.cacheControl.smallAge);

	return {
		props: {
			breadcrumbs,
			broadcasts: broadcasts.items,
			pagination: broadcasts.pagination,
			total: broadcasts.total,
			catalogMeta,
			rubricPath,
			limit: params.limit,
			rubric: rubrics.items.length ? rubrics.items[0] : null,
			urlParams: { ...params }
		}
	};
}

export default LiveSchedulePage;
